import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, CheckCircle, AlertCircle, CreditCard, Building, Hash, Calendar, Mail, Phone, MapPin, QrCode, FileText, Send } from 'lucide-react';
import { toast } from 'sonner';

const PaymentConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const paymentData = location.state;

  const [transactionDetails, setTransactionDetails] = useState({
    transactionId: '',
    paidAmount: paymentData?.invoiceAmount || '',
    invoiceNumber: paymentData?.invoiceNumber || '',
    paymentDate: new Date().toISOString().split('T')[0]
  });

  const [submitting, setSubmitting] = useState(false);

  // If no payment data, redirect back
  useEffect(() => {
    if (!paymentData) {
      toast.error('No payment data found. Please start from Make Payment page.');
      navigate('/make-payment');
    }
  }, [paymentData, navigate]);

  const bankDetails = {
    accountName: 'NETIX WORLD INTERNET SERVICES (P) LIMITED',
    accountNumber: '033311501004700',
    ifscCode: 'NESF0000333',
    bankName: 'North East Small Finance Bank',
    branchName: 'KORAMANGALA BRANCH'
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const handleSubmitPaymentDetails = async () => {
    // Validate fields
    if (!transactionDetails.transactionId) {
      toast.error('Please enter Transaction ID');
      return;
    }

    if (!transactionDetails.paidAmount) {
      toast.error('Please enter Paid Amount');
      return;
    }

    // Validate customer email
    const customerEmail = paymentData?.email || '';
    if (!customerEmail || !customerEmail.includes('@')) {
      toast.error('Valid customer email is required');
      return;
    }

    setSubmitting(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      // Prepare form data
      const formData = new FormData();
      formData.append('transactionId', transactionDetails.transactionId);
      formData.append('paidAmount', transactionDetails.paidAmount);
      formData.append('invoiceNumber', transactionDetails.invoiceNumber);
      formData.append('paymentDate', transactionDetails.paymentDate);
      formData.append('customerName', paymentData?.name || '');
      formData.append('customerMobile', paymentData?.mobile || '');
      formData.append('customerEmail', customerEmail);
      formData.append('officeName', paymentData?.officeName || '');
      formData.append('service', paymentData?.service || '');
      formData.append('address', paymentData?.address || '');

      // Send to backend
      const response = await fetch(`${API_BASE_URL}/api/payments/send-confirmation-email`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('✅ Payment confirmation email sent successfully!');
        toast.info(`📧 Email sent from ${data.sent_from} to ${data.sent_to}`);
        toast.info(`📨 Confirmation copy sent to your email: ${customerEmail}`);
        
        // Redirect to success page or home after 3 seconds
        setTimeout(() => {
          navigate('/make-payment');
        }, 3000);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send payment confirmation email');
      }
    } catch (error) {
      console.error('Email send error:', error);
      toast.error('Error sending payment confirmation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!paymentData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/make-payment')}
            className="flex items-center gap-2 text-gray-700 hover:text-red-600 transition-colors mb-4"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Make Payment
          </button>
          <div className="bg-gradient-to-r from-red-600 via-yellow-500 to-gray-400 text-white rounded-2xl p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-3 rounded-full">
                <CreditCard className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Complete Your Payment</h1>
                <p className="text-red-50 mt-1">Scan QR Code or use bank details to make payment</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left Column - QR Code & Bank Details */}
          <div className="space-y-6">
            {/* QR Code Section */}
            <div className="bg-white rounded-2xl shadow-xl border-2 border-red-200 overflow-hidden">
              <div className="bg-gradient-to-r from-red-600 to-red-500 text-white p-4">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <QrCode className="h-6 w-6" />
                  Scan QR Code to Pay
                </h2>
              </div>
              <div className="p-8">
                <div className="bg-gradient-to-br from-red-50 to-yellow-50 border-2 border-red-200 rounded-xl p-6">
                  {/* QR Code Image - Replace with actual QR code */}
                  <div className="bg-white p-6 rounded-xl border-4 border-red-300 mx-auto" style={{width: 'fit-content'}}>
                    <img 
                      src="/qr-code-payment.png" 
                      alt="Payment QR Code" 
                      className="w-64 h-64 mx-auto"
                      onError={(e) => {
                        // Fallback if image not found
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `
                          <div class="w-64 h-64 flex items-center justify-center bg-gray-100 rounded-lg">
                            <div class="text-center">
                              <div class="text-6xl mb-2">📱</div>
                              <p class="text-gray-600 font-semibold">QR Code</p>
                              <p class="text-sm text-gray-500 mt-2">Scan to pay</p>
                            </div>
                          </div>
                        `;
                      }}
                    />
                  </div>
                  
                  {/* Payment Instructions */}
                  <div className="mt-6 space-y-3">
                    <h3 className="font-bold text-gray-900 text-lg mb-3">📱 How to Pay using QR Code:</h3>
                    <div className="space-y-2 text-sm text-gray-700">
                      <div className="flex items-start gap-2">
                        <span className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                        <p>Open any UPI app (Google Pay, PhonePe, Paytm, BHIM, etc.)</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                        <p>Tap on 'Scan QR Code' or 'Scan & Pay' option</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                        <p>Scan the QR code displayed above</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
                        <p>Enter the amount: <strong>₹{paymentData.invoiceAmount}</strong></p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">5</span>
                        <p>Verify details and complete the payment</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">6</span>
                        <p>Save the Transaction ID from your payment app</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">7</span>
                        <p>Fill the transaction details below and submit</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bank Details Section */}
            <div className="bg-white rounded-2xl shadow-xl border-2 border-yellow-200 overflow-hidden">
              <div className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-white p-4">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Building className="h-6 w-6" />
                  Bank Transfer Details
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 mb-3">
                    <AlertCircle className="inline h-4 w-4 mr-2" />
                    Use these details for NEFT/RTGS/IMPS bank transfer
                  </p>

                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-3 border border-yellow-300">
                      <label className="text-xs text-gray-600 font-semibold">Account Name</label>
                      <div className="flex items-center justify-between mt-1">
                        <p className="font-bold text-gray-900">{bankDetails.accountName}</p>
                        <button
                          onClick={() => copyToClipboard(bankDetails.accountName, 'Account Name')}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-3 border border-yellow-300">
                      <label className="text-xs text-gray-600 font-semibold">Account Number</label>
                      <div className="flex items-center justify-between mt-1">
                        <p className="font-bold text-gray-900 text-xl">{bankDetails.accountNumber}</p>
                        <button
                          onClick={() => copyToClipboard(bankDetails.accountNumber, 'Account Number')}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-3 border border-yellow-300">
                      <label className="text-xs text-gray-600 font-semibold">IFSC Code</label>
                      <div className="flex items-center justify-between mt-1">
                        <p className="font-bold text-gray-900 text-xl">{bankDetails.ifscCode}</p>
                        <button
                          onClick={() => copyToClipboard(bankDetails.ifscCode, 'IFSC Code')}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-3 border border-yellow-300">
                      <label className="text-xs text-gray-600 font-semibold">Bank Name</label>
                      <p className="font-bold text-gray-900 mt-1">{bankDetails.bankName}</p>
                    </div>

                    <div className="bg-white rounded-lg p-3 border border-yellow-300">
                      <label className="text-xs text-gray-600 font-semibold">Branch Name</label>
                      <p className="font-bold text-gray-900 mt-1">{bankDetails.branchName}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-2">💳 Bank Transfer Instructions:</h3>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• Use NEFT/RTGS/IMPS for instant transfer</li>
                    <li>• Add invoice number in remarks/description</li>
                    <li>• Save transaction receipt after payment</li>
                    <li>• Note down Transaction ID (UTR number)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Payment Details Form */}
          <div>
            <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 overflow-hidden sticky top-6">
              <div className="bg-gradient-to-r from-gray-700 to-gray-600 text-white p-4">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <FileText className="h-6 w-6" />
                  Submit Payment Details
                </h2>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <span>After completing the payment, fill in your transaction details below. Click Submit to send payment confirmation email to Netix World Internet Services.</span>
                  </p>
                </div>

                {/* Transaction Details Form */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      <Hash className="inline h-4 w-4 mr-2 text-red-600" />
                      Transaction ID / UTR Number <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      value={transactionDetails.transactionId}
                      onChange={(e) => setTransactionDetails({...transactionDetails, transactionId: e.target.value})}
                      placeholder="Enter transaction ID"
                      className="w-full px-4 py-3 border-2 border-red-300 rounded-xl focus:ring-4 focus:ring-red-200 focus:border-red-500 transition-all bg-red-50"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      <CreditCard className="inline h-4 w-4 mr-2 text-yellow-600" />
                      Paid Amount (₹) <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="number"
                      value={transactionDetails.paidAmount}
                      onChange={(e) => setTransactionDetails({...transactionDetails, paidAmount: e.target.value})}
                      placeholder="Enter paid amount"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-yellow-200 focus:border-yellow-500 transition-all"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      <FileText className="inline h-4 w-4 mr-2 text-gray-600" />
                      Invoice Number
                    </label>
                    <input
                      type="text"
                      value={transactionDetails.invoiceNumber}
                      onChange={(e) => setTransactionDetails({...transactionDetails, invoiceNumber: e.target.value})}
                      placeholder="Invoice number"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-gray-200 focus:border-gray-500 transition-all bg-gray-50"
                      readOnly
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      <Calendar className="inline h-4 w-4 mr-2 text-gray-600" />
                      Payment Date
                    </label>
                    <input
                      type="date"
                      value={transactionDetails.paymentDate}
                      onChange={(e) => setTransactionDetails({...transactionDetails, paymentDate: e.target.value})}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-gray-200 focus:border-gray-500 transition-all"
                      required
                    />
                  </div>
                </div>

                {/* Customer Info Summary */}
                <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
                  <h3 className="font-bold text-gray-900 mb-3">Your Details:</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Name:</strong> {paymentData.name}</p>
                    <p><strong>Mobile:</strong> {paymentData.mobile}</p>
                    <p><strong>Service:</strong> {paymentData.service}</p>
                    {paymentData.officeName && <p><strong>Office:</strong> {paymentData.officeName}</p>}
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleSubmitPaymentDetails}
                  disabled={submitting || !transactionDetails.transactionId || !transactionDetails.paidAmount}
                  className="w-full bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-bold py-4 px-6 rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                >
                  {submitting ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Opening Email...
                    </>
                  ) : (
                    <>
                      <Send className="inline h-5 w-5 mr-2" />
                      Submit Payment Details via Email
                    </>
                  )}
                </button>

                <p className="text-xs text-center text-gray-600">
                  <Mail className="inline h-3 w-3 mr-1" />
                  Email will be sent to: <strong>care@netixfibernet.com</strong>
                </p>
              </div>
            </div>

            {/* Contact Support */}
            <div className="mt-6 bg-gradient-to-r from-red-50 to-yellow-50 border-2 border-red-200 rounded-xl p-4">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Phone className="h-5 w-5 text-red-600" />
                Need Help?
              </h3>
              <div className="space-y-2 text-sm text-gray-700">
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-red-600" />
                  <strong>Call:</strong> <span data-testid="payment-confirmation-mobile">+91 93199 19988</span>
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-red-600" />
                  <strong>Office Phone:</strong> <span data-testid="payment-confirmation-office-phone">06276 - 459015</span>
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-yellow-600" />
                  <strong>Email:</strong> info@netixfibernet.com
                </p>
                <p className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-600" />
                  <strong>Office:</strong> Delhi, Haryana, Bihar, Uttar Pradesh
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirmation;
